<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { seo } from '~/data/pages/banks/mocks'
import type { IResponseData } from '~/types'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import type { IMicroCredit } from '~/components/entity/microcredit/item'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import { navItems } from '~/data/pages/microcredit/mocks'
import { WidgetAboutPageHeader } from '~/components/widget/about-page'
import { WidgetDepositsList } from '~/components/widget/deposits'
import { useSeoFromCustom } from '~/composables/useSeoFromCustom'
import { WidgetSeoContent } from '~/components/widget/seo'

useSeoMeta(seo)
const route = useRoute()
const { t, locale } = useI18n()
const url = `/microcredit/${route.params.microcredit}`
const { data } = await useApiWith404<IResponseData<IMicroCredit>>(() => url, {
  key: `microcredit-page-${route.params.slug}`
})
const microcredit = computed(() => data.value?.data)
useEmptySectionNoindex(microcredit, 'deposits_count')

const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin

const seoData = computed(() => ({
  organization: {
    title: microcredit.value?.title
  }
}))

useSeoFromCustom(seoData, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.microcredits'), url: '/microcredit' },
  {
    title: microcredit.value?.title || t('common.microcredits'),
    url: `/microcredit/${route.params.microcredit}`
  },
  { title: t('common.deposits'), url: `/microcredit/${route.params.microcredit}/deposits` }
])

const fitOrganization = computed(() => {
  return {
    type: microcredit.value?.type || '',
    title: microcredit.value?.title || '',
    logo: microcredit.value?.logo,
    organization: {
      official_title: microcredit.value?.official_title || ''
    },
    views_count: microcredit.value?.views_count || 0,
    updated_at: microcredit.value?.updated_at || ''
  }
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="microcredit"
              :organization="microcredit"
              :type="'microcredit'"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
          </aside>
        </template>
        <div class="space-y-5">
          <WidgetAboutPageHeader
            :type="'microcredit'"
            :product="fitOrganization"
            :heading="t('common.deposits')"
          >
            <WidgetBankServicesMenu
              v-if="microcredit && microcredit.status === 'active'"
              :organization="microcredit"
              :nav-items="navItems"
              :type="'microcredit'"
            />
          </WidgetAboutPageHeader>
          <WidgetSeoContent
            heading-level="h2"
            heading-class="h3"
            leading-level="8"
            position="upper"
            :variables="{
              organization: microcredit?.official_title ?? ''
            }"
          />
          <WidgetDepositsList
            v-if="microcredit && microcredit.id && microcredit.status === 'active'"
            :hide-organization-details="true"
            :type="'microcredit'"
            :query-params="{
              per_page: '20',
              'filter[organization_id]': microcredit.id.toString(),
              'filter[organization_type]': microcredit.organization_type
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
